import { useMemo } from "react";
import { motion } from "framer-motion";
import { HeartFill, Github, Twitter, Instagram, ChevronRight } from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { QUICK_LINKS, claimFromClaims, getRoleQuickLink } from "../config/roleLinks";

const logoStyles = {
  height: "48px",
  width: "auto",
};

const SOCIALS = [
  { icon: Github, href: "https://github.com", label: "GitHub" },
  { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
  { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
];

function FooterLinkGroup({ title, links, colClassName }) {
  return (
    <div className={colClassName}>
      <div className="d-flex flex-column gap-3">
        <h6 className="footer-heading mb-0">{title}</h6>
        <div className="d-flex flex-column gap-2">
          {links.map((link) => (
            <motion.div key={link.label} whileHover={{ x: 2 }} transition={{ duration: 0.15 }}>
              <Link to={link.to} className="footer-link">
                {link.label}
                <ChevronRight size={10} className="footer-link-arrow" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Footer = () => {
  const SITE_NAME = "Teco Food Delivery";
  const currentYear = new Date().getFullYear();
  const copyrightDate = 2024 + (currentYear > 2024 ? `-${currentYear}` : "");
  const copyrightName = SITE_NAME;

  const { user, claims, isAuthenticated } = useUserContext();
  const roleLink = useMemo(() => {
    if (!isAuthenticated) return null;
    return getRoleQuickLink(claimFromClaims(claims), user);
  }, [isAuthenticated, claims, user]);

  const [aboutLink, contactLink, privacyLink, termsLink] = QUICK_LINKS;

  return (
    <motion.footer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="_footer mt-5 pt-4 pb-3 pb-md-5"
    >
      <div className="container">
        {/* Nav columns: desktop-only — mobile users get these same links on the Account page */}
        <div className="row g-4 g-md-5 d-none d-md-flex mb-4">
          <div className="col-12 col-md-4">
            <img
              className="d-block mb-2"
              src="/imgs/logo/brand-name-500.png"
              alt="Logo"
              style={logoStyles}
            />
            <p className="mb-0" style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", maxWidth: 260 }}>
              Fast, reliable food delivery across Kenya.
            </p>
            <div className="d-flex gap-2 mt-3">
              {SOCIALS.map((social) => (
                <motion.a
                  key={social.href}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.94 }}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="footer-social-icon"
                >
                  <social.icon size={15} />
                </motion.a>
              ))}
            </div>
          </div>

          <FooterLinkGroup
            title="Company"
            links={[aboutLink, contactLink]}
            colClassName="col-6 col-md-3"
          />

          <FooterLinkGroup
            title="Legal"
            links={[privacyLink, termsLink]}
            colClassName="col-6 col-md-3"
          />

          {roleLink && (
            <FooterLinkGroup
              title="Account"
              links={[{ label: roleLink.label, to: roleLink.path }]}
              colClassName="col-6 col-md-2"
            />
          )}
        </div>

        {/* Copyright Section */}
        <div className="footer-divider mb-3" />
        <div className="row">
          <div className="col-12">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 gap-md-3">
              <p className="mb-0 text-center text-md-start" style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)" }}>
                &copy; {copyrightDate} {copyrightName}
                {copyrightName.length && !copyrightName.endsWith(".") ? "." : ""} All rights reserved.
              </p>
              <p className="mb-0 d-flex align-items-center gap-1" style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)" }}>
                Made with <HeartFill size={11} className="text-danger" /> by {SITE_NAME}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;
